import {CallGroupType} from "@/api";
import ErrorHelper from "../../../utils/ErrorHelper";
import {useFormGuard} from "@/utils/formGuard";
import {FormattedPageTitle} from "../../pages";
import {Checkbox} from "@mui/material";
import {FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {ContextFilters, useAuth} from "@/context/AuthProvider";
import CallGroupSettingsForm from "./CallGroupSettingsForm";
import OutgoingCallBarringWidget from "../../../outgoing-call-barring/OutgoingCallBarringWidget";
import {formCheckboxMargin, formPagesWidth} from "@/constants";
import {getGroupTypeDesc} from "@/components/call-group/desc";
import {NumberField, TextField, EnumField} from "@dvrt/graphql-widgets";
import {DataSource, useDataSource} from "../DataSource";
import Loading from "@/components/breadcrumbs/Loading";
import {useDomainNameQuery} from "@/hooks";
import QuotaItemSelector from "@/components/QuotaItemSelector";
import {GroupLimitInfo} from "../create/Dialog";
import {limitInfoToQuotaItem, useCallGroupLimitsQuery} from "../tools";
import {defineMessage} from "react-intl";
import {CALL_GROUP_TYPE_MESSAGES} from "@/utils/translations";
import {useMemo} from "react";
import {parseInt} from "lodash";

type Props = {
    id: number;
    domainId: number;
};

type ContentProps = {
    dataSource: DataSource;
    domainName: string;
    callGroupLimits?: GroupLimitInfo;
};

const Content = ({dataSource, domainName, callGroupLimits}: ContentProps) => {
    const auth = useAuth();
    const domainContextFilter = ContextFilters.domain(domainName);
    const canEditAdmin = auth.hasPermission("edit_domain", domainContextFilter);
    const canEditOperator = auth.hasPermission("edit_superuser_group", domainContextFilter);
    const form = new CallGroupSettingsForm(dataSource.group, canEditOperator);
    useFormGuard(form.hasChanged);

    const quotaItemSelectorItems = useMemo(() => {
        if (!callGroupLimits) return [form.values.size];
        return Object.entries(callGroupLimits).map(([key, value]) => limitInfoToQuotaItem(parseInt(key), value));
    }, [callGroupLimits, form.values.size]);

    const desc = getGroupTypeDesc(dataSource.group.type);

    const errorHelper = new ErrorHelper(form.errors);

    async function submit() {
        await form.submit();
        form.newInitialData(await dataSource.refetch());
    }

    const isCallQueue = dataSource.group.type === CallGroupType.CallQueue;

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({id: "callGroupSettings", defaultMessage: "Settings for call group {name}"})}
                values={{name: form.values.name}}
            />
            <FormContainer style={{maxWidth: formPagesWidth}}>
                <FormattedFormRow label={defineMessage({id: "name", defaultMessage: "Name"})}>
                    <TextField
                        id="name"
                        disabled={!canEditAdmin}
                        value={form.values.name}
                        onChange={(value) => {
                            form.updateValues({name: value});
                        }}
                        errors={errorHelper.fieldErrors("name")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "groupSize", defaultMessage: "Group size"})}
                    hidden={isCallQueue}
                >
                    <QuotaItemSelector
                        value={form.values.size}
                        initialValue={dataSource.group.size}
                        updateValue={(size) => {
                            form.updateValues({size});
                        }}
                        items={quotaItemSelectorItems}
                        noErrorForInitialValue
                        variant="outlined"
                    />
                </FormattedFormRow>
                <FormattedFormRow label={defineMessage({id: "type", defaultMessage: "Type"})} hidden={isCallQueue}>
                    <EnumField
                        disabled={!canEditAdmin}
                        value={form.values.type}
                        enum_={{CallGroupType}}
                        onChange={(type) => {
                            form.updateValues({type});
                        }}
                        filter={(item) => (desc ? desc.types.includes(item) : true)}
                        translations={CALL_GROUP_TYPE_MESSAGES}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "pickup", defaultMessage: "Pickup group"})}
                    hidden={isCallQueue}
                >
                    <Checkbox
                        id="pickup"
                        checked={form.values.pickup}
                        onChange={(event) => form.updateValues({pickup: event.target.checked})}
                        color="primary"
                        style={{marginLeft: formCheckboxMargin}}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "sequentialTimeout", defaultMessage: "Ringing timeout (seconds)"})}
                >
                    <NumberField
                        disabled={!canEditAdmin || form.values.type === CallGroupType.Parallel}
                        fullWidth
                        variant="outlined"
                        value={form.values.sequentialTimeout}
                        max={90}
                        min={0}
                        update={(newValue) => {
                            form.updateValues({sequentialTimeout: newValue});
                        }}
                        errors={errorHelper.fieldErrors("sequentialTimeout")}
                    />
                </FormattedFormRow>
                <FormattedFormRow
                    label={defineMessage({id: "outgoing_call_barring", defaultMessage: "Outgoing call barring"})}
                    labelAlign="top"
                >
                    <OutgoingCallBarringWidget
                        masterBarrings={dataSource.domainCallBarrings}
                        operatorBarrings={form.values.outgoingCallBarringOperator}
                        adminBarrings={form.values.outgoingCallBarringAdmin}
                        updateOperatorBarrings={(barrings) =>
                            form.updateValues({outgoingCallBarringOperator: barrings})
                        }
                        updateAdminBarrings={(barrings) => form.updateValues({outgoingCallBarringAdmin: barrings})}
                        disabledAdmin={!canEditAdmin}
                        disabledOperator={!canEditOperator}
                    />
                </FormattedFormRow>
                {canEditAdmin && (
                    <FormattedFormRow>
                        <div style={{margin: "1em 0"}}>
                            <FormActionButtons
                                mainAction={submit}
                                secondaryAction={() => {
                                    form.reset();
                                }}
                                enabled={form.hasChanged && !form.submitting}
                            />
                        </div>
                    </FormattedFormRow>
                )}
            </FormContainer>
        </div>
    );
};

export default (props: Props) => {
    const {domainName, loading: domainNameLoading} = useDomainNameQuery(props.domainId);
    const {data: limitInfo, loading: callGroupLimitsLoading} = useCallGroupLimitsQuery({
        variables: {
            domainId: props.domainId,
        },
        fetchPolicy: "no-cache",
    });
    const dataSource = useDataSource(props.id, props.domainId);

    if (dataSource.loading || domainNameLoading || callGroupLimitsLoading) {
        return <Loading />;
    }

    return <Content dataSource={dataSource} domainName={domainName} callGroupLimits={limitInfo} />;
};
